import { useState, useEffect, useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

/**
 * Custom hook for infinite scrolling
 * @param {Function} fetchData - Function to fetch more data
 * @param {Object} options - Additional options
 * @returns {Object} - Infinite scroll state and ref
 */
const useInfiniteScroll = (fetchData, options = {}) => {
  const { threshold = 0.5, initialData = [], initialLoading = false } = options;
  
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [, setCursor] = useState(null);

  // Refs to hold current values for use inside callbacks without causing re-creation
  // loadingRef always starts false so the initial loadMore() call is not blocked,
  // even when the loading *state* starts true for UI purposes (initialLoading=true).
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const cursorRef = useRef(null);
  const initialLoadDone = useRef(false);

  // NOTE: refs are kept in sync inside loadMore/refresh directly.
  // Do NOT add sync effects here — they run before the initial-load effect
  // and would set loadingRef=true (because loading state starts true from
  // initialLoading:true), causing the initial loadMore() guard to bail out.

  // Setup intersection observer
  const { ref, inView } = useInView({
    threshold,
    triggerOnce: false,
  });
  
  // Load more data — stable reference; reads live values from refs
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) {
      return;
    }
    
    try {
      setLoading(true);
      loadingRef.current = true;
      
      const result = await fetchData(cursorRef.current);
      
      if (result && result.videos) {
        if (result.videos.length === 0) {
          setHasMore(false);
          hasMoreRef.current = false;
        } else {
          setData(prevData => [...prevData, ...result.videos]);
          setCursor(result.next_cursor);
          cursorRef.current = result.next_cursor;
          setHasMore(!!result.next_cursor);
          hasMoreRef.current = !!result.next_cursor;
        }
      } else {
        setHasMore(false);
        hasMoreRef.current = false;
      }
    } catch (err) {
      console.error('Error in loadMore:', err);
      setError(err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [fetchData]); // only re-create when fetchData reference changes
  
  // Refresh function to reset and reload data
  const refresh = useCallback(async () => {
    try {
      setData([]);
      setCursor(null);
      cursorRef.current = null;
      setHasMore(true);
      hasMoreRef.current = true;
      setError(null);
      setLoading(true);
      loadingRef.current = true;
      
      const result = await fetchData(null);
      
      if (result && result.videos) {
        if (result.videos.length === 0) {
          setHasMore(false);
          hasMoreRef.current = false;
        } else {
          setData(result.videos);
          setCursor(result.next_cursor);
          cursorRef.current = result.next_cursor;
          setHasMore(!!result.next_cursor);
          hasMoreRef.current = !!result.next_cursor;
        }
      } else {
        setHasMore(false);
        hasMoreRef.current = false;
      }
      
      return result;
    } catch (err) {
      console.error('Error in refresh:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [fetchData]);
  
  // Initial data load — runs once on mount only
  useEffect(() => {
    if (initialData.length === 0 && !initialLoadDone.current) {
      initialLoadDone.current = true;
      loadMore();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Load more when the sentinel comes into view
  useEffect(() => {
    if (inView) {
      loadMore();
    }
  }, [inView, loadMore]);
  
  return {
    data,
    loading,
    error,
    hasMore,
    ref,
    loadMore,
    refresh,
    setData,
  };
};

export default useInfiniteScroll;
